/// <amd-module name="Feed/Common/Protocols/Npm/RestClient/Npm" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   packaging\client\npm\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Npm from "Feed/Common/Protocols/Npm/Contracts/Npm";
import * as PackagingShared from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";

import { INpmRestClient } from "Feed/Common/Protocols/Npm/RestClient/Npm.Types";

export { INpmRestClient } from "Feed/Common/Protocols/Npm/RestClient/Npm.Types";

class NpmRestClient extends RestClientBase implements INpmRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Get scoped npm package.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the npm package (the \@scope portion of \@scope/packageName).
     * @param unscopedPackageName - Name of the package without the scope component.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getContentScopedPackage(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<ArrayBuffer> {

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "application/octet-stream",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/@{packageScope}/{unscopedPackageName}/versions/{packageVersion}/content",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get an unscoped npm package.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getContentUnscopedPackage(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<ArrayBuffer> {

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "application/octet-stream",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/{packageName}/versions/{packageVersion}/content",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Update several packages from a single feed in a single request. The updates to the packages do not happen atomically.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async updatePackages(
        batchRequest: Npm.NpmPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packagesbatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Get the Readme for a package version with an npm scope.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the package (the 'scope' part of \@scope\\name)
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope\\name)
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getReadmeScopedPackage(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<string> {

        return this.beginRequest<string>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "text/plain",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/@{packageScope}/{unscopedPackageName}/versions/{packageVersion}/readme",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get the Readme for a package version that has no npm scope.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getReadmeUnscopedPackage(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<string> {

        return this.beginRequest<string>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "text/plain",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/{packageName}/versions/{packageVersion}/readme",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Delete or restore several package versions from the recycle bin.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async updateRecycleBinPackages(
        batchRequest: Npm.NpmPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/PackagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete a package version with an npm scope from the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the package (the 'scope' part of \@scope/name).
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope/name).
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deleteScopedPackageVersionFromRecycleBin(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/packages/@{packageScope}/{unscopedPackageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get information about a scoped package version in the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the package (the 'scope' part of \@scope/name)
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope/name).
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getScopedPackageVersionMetadataFromRecycleBin(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.NpmPackageVersionDeletionState> {

        return this.beginRequest<Npm.NpmPackageVersionDeletionState>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/packages/@{packageScope}/{unscopedPackageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Restore a package version with an npm scope from the recycle bin to its feed.
     * 
     * @param packageVersionDetails - 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the package (the 'scope' part of \@scope/name).
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope/name).
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async restoreScopedPackageVersionFromRecycleBin(
        packageVersionDetails: Npm.NpmRecycleBinPackageVersionDetails,
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/packages/@{packageScope}/{unscopedPackageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

    /**
     * Delete a package version without an npm scope from the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersionFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get information about an unscoped package version in the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getPackageVersionMetadataFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.NpmPackageVersionDeletionState> {

        return this.beginRequest<Npm.NpmPackageVersionDeletionState>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Restore a package version without an npm scope from the recycle bin to its feed.
     * 
     * @param packageVersionDetails - 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async restorePackageVersionFromRecycleBin(
        packageVersionDetails: Npm.NpmRecycleBinPackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

    /**
     * Get the upstreaming behavior of the (scoped) package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageScope - The scope of the package
     * @param unscopedPackageName - The name of the scoped package
     * @param project - Project ID or project name
     */
    public async getScopedUpstreamingBehavior(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        project?: string
        ): Promise<PackagingShared.UpstreamingBehavior> {

        return this.beginRequest<PackagingShared.UpstreamingBehavior>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/@{packageScope}/{unscopedPackageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName
            }
        });
    }

    /**
     * Set the upstreaming behavior of a (scoped) package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageScope - The scope of the package
     * @param unscopedPackageName - The name of the scoped package
     * @param behavior - The behavior to apply to the scoped package within the scope of the feed
     * @param project - Project ID or project name
     */
    public async setScopedUpstreamingBehavior(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        behavior: PackagingShared.UpstreamingBehavior,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/@{packageScope}/{unscopedPackageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName
            },
            body: behavior
        });
    }

    /**
     * Get the upstreaming behavior of the (unscoped) package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageName - The name of the package
     * @param project - Project ID or project name
     */
    public async getUpstreamingBehavior(
        feedId: string,
        packageName: string,
        project?: string
        ): Promise<PackagingShared.UpstreamingBehavior> {

        return this.beginRequest<PackagingShared.UpstreamingBehavior>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/{packageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName
            }
        });
    }

    /**
     * Set the upstreaming behavior of a (scoped) package within the context of a feed
     * 
     * @param feedId - The name or id of the feed
     * @param packageName - The name of the package
     * @param behavior - The behavior to apply to the scoped package within the scope of the feed
     * @param project - Project ID or project name
     */
    public async setUpstreamingBehavior(
        feedId: string,
        packageName: string,
        behavior: PackagingShared.UpstreamingBehavior,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/packages/{packageName}/upstreaming",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName
            },
            body: behavior
        });
    }

    /**
     * Get information about a scoped package version (such as \@scope/name).
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the package (the 'scope' part of \@scope/name).
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope/name).
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getScopedPackageInfo(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.Package> {

        return this.beginRequest<Npm.Package>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/@{packageScope}/{unscopedPackageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Unpublish a scoped package version (such as \@scope/name).
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageScope - Scope of the package (the 'scope' part of \@scope/name).
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope/name).
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async unpublishScopedPackage(
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.Package> {

        return this.beginRequest<Npm.Package>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/@{packageScope}/{unscopedPackageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Update state for an npm scoped package version.
     * 
     * @param packageVersionDetails - Updated state for the package version.
     * @param feedId - Name or ID of the feed.
     * @param packageScope - 
     * @param unscopedPackageName - Name of the package (the 'name' part of \@scope/name).
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async updateScopedPackage(
        packageVersionDetails: Npm.PackageVersionDetails,
        feedId: string,
        packageScope: string,
        unscopedPackageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.Package> {

        return this.beginRequest<Npm.Package>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/@{packageScope}/{unscopedPackageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageScope: packageScope,
                unscopedPackageName: unscopedPackageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

    /**
     * Get information about an unscoped package version.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getPackageInfo(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.Package> {

        return this.beginRequest<Npm.Package>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/{packageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Unpublish an unscoped package version.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async unpublishPackage(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.Package> {

        return this.beginRequest<Npm.Package>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/{packageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Update state for an unscoped package version.
     * 
     * @param packageVersionDetails - Updated state for the package version.
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async updatePackage(
        packageVersionDetails: Npm.PackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<Npm.Package> {

        return this.beginRequest<Npm.Package>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/npm/{packageName}/Versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

}

export const NpmClientName = "INpmRestClient";

export function getNpmClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): INpmRestClient {
    return pageContext.getRestClient<INpmRestClient>(NpmClientName, options);
}

RestClients.add(NpmClientName, { 
    factory: NpmRestClient,
    options: {
        resourceAreaId: "4c83cfc1-f33a-477e-a789-29d38ffca52e",
        serviceInstanceType: "00000030-0000-8888-8000-000000000000"
    }
});
